import { vibeForLevel, VIBES } from './MusicVibes.js';
/**
 * Everything you hear is synthesised at runtime. No audio files means no load
 * time, no decode stalls, and sounds that can be parameterised by gameplay
 * (impact speed, fall speed, music intensity) instead of picked from a list.
 */

const A = 55; // low A, the root of everything

/** Semitone offsets from the root, natural minor with a flat second. */
const SCALE = [0, 2, 3, 5, 7, 8, 10];

const noteHz = (semitone, octave = 0) => A * Math.pow(2, (semitone + octave * 12) / 12);
const scaleHz = (degree, octave = 0) => noteHz(SCALE[((degree % 7) + 7) % 7], octave + Math.floor(degree / 7));

export class AudioEngine {
  constructor(settings) {
    this.settings = settings;
    this.ctx = null;
    this.ready = false;
    this.started = false;
    this._noise = null;
    this._musicTimer = null;
    this._nextNote = 0;
    this._step = 0;
    this.intensity = 0;
    this._targetIntensity = 0;
    this.tempo = 132;
    this.vibe = { name: 'surge', ...VIBES.surge };
  }

  // --------------------------------------------------------------- lifecycle

  init() {
    if (this.ctx) return;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) { console.warn('[audio] Web Audio unavailable'); return; }
    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.9;

    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 12;
    this.limiter.ratio.value = 8;
    this.limiter.attack.value = 0.004;
    this.limiter.release.value = 0.18;

    this.sfxBus = ctx.createGain();
    this.musicBus = ctx.createGain();
    // Everything musical except the kick rides the duck: the kick fires
    // straight through and shoves this bus down, which is the sidechain
    // pump the whole genre is built on.
    this.duckBus = ctx.createGain();
    this.duckBus.connect(this.musicBus);
    this.sfxBus.connect(this.master);
    this.musicBus.connect(this.master);
    this.master.connect(this.limiter);
    this.limiter.connect(ctx.destination);

    // Shared send effects: one reverb-ish delay network for everything.
    this.delay = ctx.createDelay(1.0);
    this.delay.delayTime.value = 60 / this.tempo * 0.75;
    this.delayFb = ctx.createGain();
    this.delayFb.gain.value = 0.38;
    this.delayFilter = ctx.createBiquadFilter();
    this.delayFilter.type = 'lowpass';
    this.delayFilter.frequency.value = 2600;
    this.delaySend = ctx.createGain();
    this.delaySend.gain.value = 1;
    this.delaySend.connect(this.delay);
    this.delay.connect(this.delayFilter);
    this.delayFilter.connect(this.delayFb);
    this.delayFb.connect(this.delay);
    this.delayFilter.connect(this.master);

    this._buildNoise();
    this._buildAmbience();
    this.applySettings();
    this.ready = true;
  }

  /** Browsers require a gesture; call this from the first tap or key press. */
  async unlock() {
    this.init();
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch (e) { /* user will retry */ }
    }
  }

  applySettings() {
    if (!this.ctx) return;
    const s = this.settings.values;
    this.musicBus.gain.setTargetAtTime((s.music ?? 55) / 100 * 0.55, this.ctx.currentTime, 0.05);
    this.sfxBus.gain.setTargetAtTime((s.sfx ?? 80) / 100 * 0.9, this.ctx.currentTime, 0.05);
  }

  _buildNoise() {
    const ctx = this.ctx;
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      // Light pinking makes it sit better under everything else.
      last = 0.98 * last + 0.02 * white;
      d[i] = white * 0.7 + last * 3.2;
    }
    this._noise = buf;
  }

  noiseSource(loop = false) {
    const src = this.ctx.createBufferSource();
    src.buffer = this._noise;
    src.loop = loop;
    return src;
  }

  // --------------------------------------------------------------- ambience

  _buildAmbience() {
    const ctx = this.ctx;

    // Wind, driven by player speed.
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 500;
    this.windFilter.Q.value = 0.7;
    const wind = this.noiseSource(true);
    wind.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.sfxBus);
    wind.start();
    this._wind = wind;

    // Structure drone.
    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0.0;
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 220;
    for (const [f, d] of [[41.2, 0], [61.7, 0.4], [82.4, 0.8]]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = d * 9;
      const g = ctx.createGain();
      g.gain.value = 0.16;
      o.connect(g); g.connect(droneFilter);
      o.start();
    }
    droneFilter.connect(this.droneGain);
    this.droneGain.connect(this.musicBus);
  }

  setAmbience({ speed = 0, active = true }) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const s = Math.min(1, speed / 55);
    this.windGain.gain.setTargetAtTime(active ? Math.pow(s, 1.7) * 0.5 : 0, t, 0.12);
    this.windFilter.frequency.setTargetAtTime(360 + s * 1500, t, 0.15);
    this.droneGain.gain.setTargetAtTime(active ? 0.22 : 0.05, t, 0.4);
    this._targetIntensity = Math.min(1, speed / 42);
  }

  // ------------------------------------------------------------------- sfx

  _env(node, when, attack, decay, peak = 1) {
    const g = node.gain;
    g.setValueAtTime(0.0001, when);
    g.exponentialRampToValueAtTime(Math.max(peak, 0.0002), when + attack);
    g.exponentialRampToValueAtTime(0.0001, when + attack + decay);
  }

  _tone({ type = 'sine', f0, f1, dur = 0.2, attack = 0.005, peak = 0.4, dest = null, detune = 0 }) {
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = type;
    o.detune.value = detune;
    o.frequency.setValueAtTime(f0, t);
    if (f1 && f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(f1, 1), t + dur);
    const g = ctx.createGain();
    this._env(g, t, attack, dur - attack, peak);
    o.connect(g);
    g.connect(dest || this.sfxBus);
    o.start(t);
    o.stop(t + dur + 0.05);
    return { o, g };
  }

  _noiseBurst({ dur = 0.2, f0 = 3000, f1 = 300, q = 2, peak = 0.4, type = 'bandpass', dest = null }) {
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const src = this.noiseSource();
    const filt = ctx.createBiquadFilter();
    filt.type = type;
    filt.Q.value = q;
    filt.frequency.setValueAtTime(f0, t);
    filt.frequency.exponentialRampToValueAtTime(Math.max(f1, 20), t + dur);
    const g = ctx.createGain();
    this._env(g, t, 0.004, dur, peak);
    src.connect(filt); filt.connect(g); g.connect(dest || this.sfxBus);
    src.start(t);
    src.stop(t + dur + 0.05);
    return { src, g, filt };
  }

  portalFire(which = 'a') {
    if (!this.ready) return;
    const base = which === 'a' ? 880 : 660;
    this._tone({ type: 'sawtooth', f0: base * 2.2, f1: base * 0.5, dur: 0.16, peak: 0.16 });
    this._noiseBurst({ dur: 0.13, f0: 5200, f1: 900, q: 1.4, peak: 0.2 });
  }

  portalOpen(which = 'a') {
    if (!this.ready) return;
    const base = which === 'a' ? 330 : 262;
    this._tone({ type: 'triangle', f0: base * 0.6, f1: base * 2.4, dur: 0.42, peak: 0.2, dest: this.delaySend });
    this._tone({ type: 'sine', f0: base * 3, f1: base * 4.5, dur: 0.3, peak: 0.1 });
    this._noiseBurst({ dur: 0.5, f0: 400, f1: 3400, q: 3, peak: 0.14 });
  }

  portalDeny() {
    if (!this.ready) return;
    this._tone({ type: 'square', f0: 150, f1: 70, dur: 0.18, peak: 0.14 });
    this._noiseBurst({ dur: 0.12, f0: 700, f1: 200, q: 6, peak: 0.1 });
  }

  teleport(speed = 10) {
    if (!this.ready) return;
    const s = Math.min(1, speed / 50);
    this._noiseBurst({ dur: 0.34 + s * 0.2, f0: 260, f1: 5200 * (0.5 + s), q: 1.1, peak: 0.16 + s * 0.2 });
    this._tone({ type: 'sine', f0: 160 + s * 200, f1: 900 + s * 1400, dur: 0.24, peak: 0.16, dest: this.delaySend });
  }

  jump() {
    if (!this.ready) return;
    this._tone({ type: 'triangle', f0: 300, f1: 520, dur: 0.11, peak: 0.1 });
  }

  land(impact = 10) {
    if (!this.ready) return;
    const s = Math.min(1, impact / 45);
    this._tone({ type: 'sine', f0: 150 + s * 60, f1: 45, dur: 0.16 + s * 0.2, peak: 0.16 + s * 0.35 });
    this._noiseBurst({ dur: 0.1 + s * 0.14, f0: 1400, f1: 200, q: 1.2, peak: 0.1 + s * 0.24 });
  }

  step() {
    if (!this.ready) return;
    this._noiseBurst({ dur: 0.055, f0: 2200, f1: 700, q: 2.5, peak: 0.055 });
  }

  boost() {
    if (!this.ready) return;
    this._tone({ type: 'sawtooth', f0: 180, f1: 1500, dur: 0.36, peak: 0.2, dest: this.delaySend });
    this._noiseBurst({ dur: 0.3, f0: 300, f1: 4200, q: 1.5, peak: 0.18 });
  }

  pickup(index = 0) {
    if (!this.ready) return;
    const f = scaleHz(index + 7, 3);
    this._tone({ type: 'square', f0: f, f1: f, dur: 0.09, peak: 0.09 });
    this._tone({ type: 'sine', f0: f * 2, f1: f * 2, dur: 0.28, peak: 0.11, dest: this.delaySend });
  }

  checkpoint() {
    if (!this.ready) return;
    this._tone({ type: 'triangle', f0: scaleHz(4, 3), dur: 0.14, peak: 0.11 });
    setTimeout(() => this.ready && this._tone({
      type: 'triangle', f0: scaleHz(7, 3), dur: 0.3, peak: 0.11, dest: this.delaySend,
    }), 110);
  }

  death() {
    if (!this.ready) return;
    this._tone({ type: 'sawtooth', f0: 420, f1: 40, dur: 0.7, peak: 0.22 });
    this._noiseBurst({ dur: 0.6, f0: 2600, f1: 90, q: 0.9, peak: 0.2 });
  }

  victory() {
    if (!this.ready) return;
    const degs = [0, 2, 4, 7];
    degs.forEach((d, i) => setTimeout(() => {
      if (!this.ready) return;
      this._tone({ type: 'triangle', f0: scaleHz(d, 3), dur: 0.5, peak: 0.14, dest: this.delaySend });
      this._tone({ type: 'sine', f0: scaleHz(d, 4), dur: 0.4, peak: 0.09 });
    }, i * 110));
  }

  fail() {
    if (!this.ready) return;
    [0, -1, -3].forEach((d, i) => setTimeout(() => {
      if (!this.ready) return;
      this._tone({ type: 'sawtooth', f0: scaleHz(d, 1), dur: 0.5, peak: 0.16 });
    }, i * 170));
  }

  ui(kind = 'click') {
    if (!this.ready) return;
    if (kind === 'back') this._tone({ type: 'square', f0: 420, f1: 260, dur: 0.07, peak: 0.06 });
    else if (kind === 'hover') this._tone({ type: 'sine', f0: 1400, dur: 0.04, peak: 0.025 });
    else this._tone({ type: 'square', f0: 700, f1: 1100, dur: 0.06, peak: 0.07 });
  }

  // ----------------------------------------------------------------- music

  /** Select the level's mood; safe to call any time, applies at once. */
  setVibe(def) {
    this.vibe = vibeForLevel(def);
    this.tempo = this.vibe.bpm;
    if (this.ctx) this.delay.delayTime.value = 60 / this.tempo * 0.75;
  }

  startMusic() {
    if (!this.ready || this.started) return;
    this.started = true;
    this._nextNote = this.ctx.currentTime + 0.1;
    this._step = 0;
    this._musicTimer = setInterval(() => this._schedule(), 25);
  }

  stopMusic() {
    this.started = false;
    if (this._musicTimer) { clearInterval(this._musicTimer); this._musicTimer = null; }
  }

  /** Lookahead scheduler: queue anything due in the next 120ms. */
  _schedule() {
    if (!this.ctx) return;
    const spb = 60 / this.tempo;
    const stepDur = spb / 4;
    this.intensity += (this._targetIntensity - this.intensity) * 0.08;
    while (this._nextNote < this.ctx.currentTime + 0.12) {
      this._playStep(this._step, this._nextNote, stepDur);
      this._nextNote += stepDur;
      this._step++;
    }
  }

  /** Root-relative note frequency for the current vibe. */
  _vf(semi, oct = 0) {
    return noteHz(this.vibe.root + semi, oct);
  }

  _playStep(step, when, stepDur) {
    const v = this.vibe;
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const s32 = step % 32;
    const intensity = this.intensity;
    const th = v.thresholds;

    // --- kick (with sidechain pump); half-time vibes breathe on 1 and 3
    const kickOn = v.halfTime ? (s === 0 || s === 8) : s % 4 === 0;
    if (kickOn) this._drumKick(when, v.kick);
    if (v.clap && (s === 4 || s === 12)) this._drumClap(when, 0.26);

    // --- hats: offbeats always, sixteenths once moving
    if (s % 4 === 2) this._drumHat(when, 0.12, intensity > th.hats16 && s % 8 === 6);
    if (intensity > th.hats16 && s % 2 === 1) this._drumHat(when, 0.07, false);

    // --- bass + sub, the pattern is the vibe's signature
    const note = v.bass[s32];
    if (note >= 0 && intensity > th.bass) {
      this._bass(when, this._vf(note, 0), stepDur * 1.6, 0.42 + 0.22 * intensity, v.sub);
    }

    // --- stabs
    if (intensity > th.stabs && v.stabs[s32]) this._stab(when, v.stabs[s32]);

    // --- acid line at the top of the ramp
    if (v.acid && intensity > th.acid) this._acid(when, v.acid[step % 8], step);

    // --- pads for the airy vibes
    if (v.pads && s === 0 && bar % 2 === 0) {
      const rootDeg = [0, 0, 5, 3][(bar / 2) % 4 | 0] ?? 0;
      this._pad(when, rootDeg, stepDur * 32);
    }
  }

  _stab(when, semis) {
    const ctx = this.ctx;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass'; f.Q.value = 5;
    f.frequency.setValueAtTime(3600, when);
    f.frequency.exponentialRampToValueAtTime(480, when + 0.2);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(0.13, when + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.22);
    for (const semi of semis) {
      for (const det of [-9, 8]) {
        const o = ctx.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = this._vf(semi, 2);
        o.detune.value = det;
        o.connect(f);
        o.start(when); o.stop(when + 0.26);
      }
    }
    f.connect(g); g.connect(this.duckBus);
  }

  _acid(when, semi, step) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = this._vf(semi, 1);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass'; f.Q.value = 11;
    const sweep = 500 + 2600 * (0.4 + 0.6 * Math.sin(step * 0.42));
    f.frequency.setValueAtTime(sweep, when);
    f.frequency.exponentialRampToValueAtTime(sweep * 0.4, when + 0.1);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(0.11, when + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.1);
    o.connect(f); f.connect(g); g.connect(this.duckBus);
    o.start(when); o.stop(when + 0.12);
  }

  _drumKick(when, k) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(k.f0, when);
    o.frequency.exponentialRampToValueAtTime(k.f1, when + 0.11);
    const g = ctx.createGain();
    g.gain.setValueAtTime(k.peak, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + k.decay);
    o.connect(g); g.connect(this.musicBus);
    o.start(when); o.stop(when + k.decay + 0.05);
    // The click that survives phone speakers.
    const n = this.noiseSource();
    const nf = ctx.createBiquadFilter(); nf.type = 'highpass'; nf.frequency.value = 950;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(k.peak * 0.5, when);
    ng.gain.exponentialRampToValueAtTime(0.0001, when + 0.03);
    n.connect(nf); nf.connect(ng); ng.connect(this.musicBus);
    n.start(when); n.stop(when + 0.05);
    // Sidechain: shove everything else down, let it breathe back.
    this.duckBus.gain.cancelScheduledValues(when);
    this.duckBus.gain.setValueAtTime(0.22, when);
    this.duckBus.gain.exponentialRampToValueAtTime(1.0, when + 0.24);
  }

  _drumClap(when, peak) {
    const ctx = this.ctx;
    const src = this.noiseSource();
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass'; f.frequency.value = 1700; f.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(peak, when + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.16);
    src.connect(f); f.connect(g); g.connect(this.duckBus);
    src.start(when); src.stop(when + 0.2);
  }

  _drumHat(when, peak, open = false) {
    const ctx = this.ctx;
    const src = this.noiseSource();
    const f = ctx.createBiquadFilter();
    f.type = 'highpass'; f.frequency.value = 7200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + (open ? 0.15 : 0.04));
    src.connect(f); f.connect(g); g.connect(this.duckBus);
    src.start(when); src.stop(when + (open ? 0.18 : 0.08));
  }

  _bass(when, freq, dur, peak, subLevel = 0.5) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = freq;
    const o2 = ctx.createOscillator();
    o2.type = 'square';
    o2.frequency.value = freq * 0.5;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = 4;
    f.frequency.setValueAtTime(240 + this.intensity * 900, when);
    f.frequency.exponentialRampToValueAtTime(100, when + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(peak, when + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o.connect(f); o2.connect(f); f.connect(g); g.connect(this.duckBus);
    o.start(when); o.stop(when + dur + 0.05);
    o2.start(when); o2.stop(when + dur + 0.05);
    // The requested low end: a dedicated sub sine an octave down, clean of
    // the filter so the floor never thins out.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = freq * 0.5;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.0001, when);
    sg.gain.exponentialRampToValueAtTime(peak * subLevel, when + 0.012);
    sg.gain.exponentialRampToValueAtTime(0.0001, when + dur * 1.15);
    sub.connect(sg); sg.connect(this.duckBus);
    sub.start(when); sub.stop(when + dur * 1.15 + 0.05);
  }

  _arp(when, freq, dur, peak) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = freq;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1400 + this.intensity * 4200;
    f.Q.value = 3;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(peak, when + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o.connect(f); f.connect(g);
    g.connect(this.musicBus);
    g.connect(this.delaySend);
    o.start(when); o.stop(when + dur + 0.05);
  }

  _pad(when, rootDeg, dur) {
    const ctx = this.ctx;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(300, when);
    f.frequency.linearRampToValueAtTime(1500, when + dur * 0.5);
    f.frequency.linearRampToValueAtTime(320, when + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.linearRampToValueAtTime(0.055, when + dur * 0.25);
    g.gain.linearRampToValueAtTime(0.0001, when + dur);
    f.connect(g); g.connect(this.duckBus);
    for (const [d, det] of [[rootDeg, -7], [rootDeg + 2, 5], [rootDeg + 4, -3]]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = scaleHz(d, 1);
      o.detune.value = det;
      o.connect(f);
      o.start(when); o.stop(when + dur + 0.1);
    }
  }

  dispose() {
    this.stopMusic();
    try { this._wind?.stop(); } catch (e) { /* already stopped */ }
    this.ctx?.close();
    this.ctx = null;
    this.ready = false;
  }
}
